export function hasTokenProjectEditAccess({ siteAdmin, companyRoles = [], projectOverrides = [], projectID, companyID }) {
  if (siteAdmin) {
    return true;
  }

  const hasProjectOverride = Array.isArray(projectOverrides) && projectOverrides.some((override) => (
    override && override.projectID == projectID && override.canEdit === true
  ));

  if (hasProjectOverride) {
    return true;
  }

  const hasCompanyEditorRole = Array.isArray(companyRoles) && companyRoles.some((companyRole) => (
    companyRole && companyRole.companyID == companyID && ['admin', 'editor'].includes(companyRole.role)
  ));

  return hasCompanyEditorRole;
}

export function selectHasEditorAccess(state) {
  return !!(state.user.siteAdmin || state.ui.canEditProject);
}

export function selectIsSiteAdmin(state) {
  return !!state.user.siteAdmin;
}

export function selectIsTrueSiteAdmin(state) {
  return !!state.user.siteAdmin && Number(state.user.siteStaffLevel) === 1;
}

export function canAddPasteModels(siteAdmin) {
  return !!siteAdmin;
}

// Explicit capability helper for legacy site-staff-level gated actions.
// Current policy keeps behavior unchanged: capability requires siteAdmin + level 1.
export function canManageMasterReview(siteAdmin, siteStaffLevel) {
  return !!siteAdmin && Number(siteStaffLevel) === 1;
}

export function selectCanManageMasterReview(state) {
  return canManageMasterReview(state.user.siteAdmin, state.user.siteStaffLevel);
}

export function canCreateVideoRecording(siteAdmin, siteStaffLevel) {
  return !!siteAdmin && Number(siteStaffLevel) === 1;
}

export function hasEditorAccess(siteAdmin, canEditProject) {
  return !!(siteAdmin || canEditProject);
}

// Consolidated UI capabilities for gradual migration away from raw role checks.
export function selectPermissionCapabilities(state) {
  const isSiteAdmin = selectIsSiteAdmin(state);
  const isTrueSiteAdmin = selectIsTrueSiteAdmin(state);
  const canEditProject = !!state.ui.canEditProject;

  return {
    isSiteAdmin,
    isTrueSiteAdmin,
    canEditProject,
    canAccessEditorUI: hasEditorAccess(isSiteAdmin, canEditProject),
    canAddPasteModels: canAddPasteModels(isSiteAdmin),
    canManageMasterReview: selectCanManageMasterReview(state),
    canCreateVideoRecording: canCreateVideoRecording(isSiteAdmin, state.user.siteStaffLevel)
  };
}

// Centralized editor modal access policy to prevent UI permission regressions.
// Rules:
// - editMode is required for all editor modals except Help/Settings.
// - project editors (canEditProject) can access Labels/Lights/Clipping/World.
// - site admins only can access Advanced Scene + Legacy Slide editor.
export function canAccessEditorModal(modalName, { siteAdmin, canEditProject, editMode }) {
  const alwaysAllowed = new Set(['HelpInfo', 'SettingsEditor']);
  const projectEditorAllowed = new Set([
    'InventumLabelEditor',
    'InventumLightsEditor',
    'InventumClippingEditor',
    'InventumWorldEditor'
  ]);
  const siteAdminOnly = new Set(['InventumSlideEditor', 'InventumSceneFileEditor']);

  if (alwaysAllowed.has(modalName)) {
    return true;
  }

  if (!editMode) {
    return false;
  }

  if (siteAdminOnly.has(modalName)) {
    return !!siteAdmin;
  }

  if (projectEditorAllowed.has(modalName)) {
    return hasEditorAccess(siteAdmin, canEditProject);
  }

  // Deny unknown editor modal names by default.
  return false;
}
